import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Paper,
    IconButton,
    Chip,
    Button,
    Tooltip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    SelectChangeEvent,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import { Plus, Minus, Package, Eye, RefreshCw, Search, ChevronLeft, ChevronRight, List, X, ChevronsLeft, ChevronsRight, Boxes, Building2, ChevronDown } from 'lucide-react';
import DoublePackageIcon from '../icons/DoublePackageIcon';

import './Routing.css';
import { useUserPermissions } from '../context/UserPermissionsContext';
import { isRouteFlagEnabled, isIndicatorEnabled } from '../services/RouteService';
import { useRoutingState } from './useRoutingState';
import { getCurrentDateForDC } from '../utils/dcTimeZones';
import { broadcastDCChange } from '../utils/dcEvents';

// Font family constant for consistency
const FONT_FAMILY = '"Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';
const ROUTE_DETAILS_TAB_INDEX = 0;
const SPLIT_WORKSHEET_TAB_INDEX = 1;

// Memoized date formatter for performance
// Keep API wall-clock time as-is to avoid timezone shifts between tabs.
const formatDate = (dateString: string) => {
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (!match) {
        return dateString;
    }

    const [, year, month, day, hour, minute, second] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));

    const dateStr = parsed.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });

    return dateStr;
};

const getCalendarDateKey = (dateString: string) => {
    // Backend sends timestamps already converted to DC timezone
    // Extract date portion directly from ISO string (YYYY-MM-DD)
    // Don't use new Date() as it interprets naive timestamps as UTC
    const match = dateString.match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : '';
};

const formatCalendarDate = (dateKey: string) => {
    const [year, month, day] = dateKey.split('-').map(Number);

    return new Date(year, (month || 1) - 1, day || 1).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
    });
};

// Column width mappings
const COLUMN_WIDTHS = {
    route: '90px',
    filename: '280px',
    date: '390px',
    status: '120px',
    routeTypes: '180px',
    remark: '190px',
    actions: '100px'
};

// Status filter options
const STATUS_OPTIONS = [
    { value: 'success', label: 'SUCCESS', color: '#4caf50' },
    { value: 'in progress', label: 'IN PROGRESS', color: '#ffb74d' },
    { value: 'failed', label: 'FAILED', color: '#f44336' }
];

// Common styles
const COMMON_STYLES = {
    tableCell: {
        fontSize: '0.75rem',
        fontFamily: FONT_FAMILY,
        border: '1px solid #e0e0e0',
        py: 0.5,
        px: 1
    },
    filterTextField: {
        mt: 1,
        width: '100%',
        '& .MuiInputBase-root': {
            fontSize: '0.7rem',
            height: '28px',
            fontFamily: FONT_FAMILY
        }
    },
    smallButton: {
        minWidth: 'auto',
        fontSize: '0.7rem',
        padding: '4px 8px',
        textTransform: 'none',
        fontFamily: FONT_FAMILY
    }
};

const DATE_FILTER_FIELD_SX = {
    flex: '1 1 132px',
    minWidth: '132px',
    '& .MuiInputBase-root': {
        fontSize: 'clamp(0.54rem, 0.6vw, 0.71rem)',
        height: '27px',
        fontFamily: FONT_FAMILY,
        fontWeight: 600,
        background: 'linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)'
    },
    '& .MuiInputBase-input::placeholder': {
        fontSize: 'clamp(0.5rem, 0.55vw, 0.64rem)',
        letterSpacing: '-0.01em',
        opacity: 1
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        transition: 'all 0.22s ease',
        '& fieldset': {
            borderColor: '#b3d4f5'
        },
        '&:hover fieldset': {
            borderColor: '#4f9de0'
        },
        '&.Mui-focused fieldset': {
            borderColor: '#156fcd',
            borderWidth: '1px'
        },
        '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(21, 111, 205, 0.16), 0 6px 14px rgba(21, 111, 205, 0.14)'
        }
    },
    '& .MuiInputAdornment-root': { marginLeft: '0px' },
    '& .MuiIconButton-root': {
        padding: '1px',
        color: '#1d4f91',
        borderRadius: '6px',
        transition: 'background-color 0.18s ease',
        '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.1)'
        }
    }
};

const DATE_PICKER_PAPER_SX = {
    borderRadius: '12px',
    border: '1px solid #d1e5fb',
    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.2)',
    overflow: 'hidden',
    '& .MuiPickersLayout-root': {
        background: 'linear-gradient(160deg, #f4f9ff 0%, #ffffff 44%, #f8fbff 100%)',
        minWidth: '228px'
    },
    '& .MuiPickersLayout-contentWrapper': {
        maxHeight: '265px'
    },
    '& .MuiDateCalendar-root': {
        width: '204px',
        maxHeight: '228px'
    },
    '& .MuiPickersCalendarHeader-root': {
        px: 0.75,
        py: 0.5,
        borderBottom: '1px solid #ddeaf9',
        background: 'linear-gradient(90deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,0.85) 70%)'
    },
    '& .MuiPickersCalendarHeader-label': {
        fontWeight: 700,
        color: '#1f3f63'
    },
    '& .MuiDayCalendar-header': {
        mb: 0.15,
        '& .MuiTypography-root': {
            color: '#5f748b',
            fontWeight: 700,
            fontSize: '0.62rem'
        }
    },
    '& .MuiPickersDay-root': {
        borderRadius: '8px',
        width: '25px',
        height: '25px',
        margin: '0 1px',
        fontSize: '0.7rem',
        transition: 'background-color 0.2s ease, color 0.2s ease',
        '&:hover': {
            backgroundColor: '#e8f3ff',
            color: '#12467d'
        },
        '&.Mui-selected': {
            background: 'linear-gradient(180deg, #2684df 0%, #156fcd 100%)',
            color: '#fff'
        },
        '&.Mui-selected:hover': {
            background: 'linear-gradient(180deg, #1f7bd8 0%, #0f63bb 100%)'
        }
    },
    '& .MuiMultiSectionDigitalClock-root': {
        borderTop: '1px solid #dce9f8',
        background: 'linear-gradient(180deg, #fafdff 0%, #f4f9ff 100%)',
        height: '232px'
    },
    '& .MuiMultiSectionDigitalClockSection-root': {
        width: '60px',
        '&:not(:last-of-type)': {
            borderRight: '1px solid #e8f0fa'
        },
        '& ul': {
            maxHeight: '200px'
        }
    },
    '& .MuiMultiSectionDigitalClockSection-root:last-of-type': {
        width: '54px'
    },
    '& .MuiMenuItem-root': {
        borderRadius: '8px',
        margin: '1px 3px',
        minHeight: '25px',
        fontSize: '0.7rem',
        fontWeight: 600,
        color: '#223242',
        '&:hover': {
            backgroundColor: '#eef5ff'
        },
        '&.Mui-selected': {
            background: 'linear-gradient(180deg, #ddebff 0%, #d3e7ff 100%)',
            color: '#0d4a8e',
            fontWeight: 800
        }
    },
    '& .MuiPickersLayout-actionBar': {
        display: 'flex',
        justifyContent: 'flex-end',
        py: 0.45,
        px: 0.8,
        borderTop: '1px solid #dce9f8',
        backgroundColor: '#ffffff'
    },
    '& .MuiPickersLayout-actionBar .MuiButton-root': {
        borderRadius: '999px',
        textTransform: 'none',
        fontWeight: 800,
        minHeight: '24px',
        minWidth: '46px',
        px: 1.4,
        fontSize: '0.7rem',
        letterSpacing: '0.2px',
        color: '#ffffff !important',
        background: 'linear-gradient(180deg, #2684df 0%, #156fcd 100%)',
        boxShadow: '0 4px 10px rgba(21, 111, 205, 0.28)',
        '&:hover': {
            background: 'linear-gradient(180deg, #1f7bd8 0%, #0f63bb 100%)',
            boxShadow: '0 6px 14px rgba(15, 99, 187, 0.36)'
        }
    },
    '& ul': {
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' }
    },
    '& .MuiPickersDay-today:not(.Mui-selected)': {
        border: 'none !important'
    }
};

// Helper function to get store background styling based on the entire store name
const getStoreBackgroundStyle = (store: string) => {
    if (!store) return { backgroundColor: 'transparent' };

    // Create a simple hash of the store string for consistent color assignment
    let hash = 0;
    for (let i = 0; i < store.length; i++) {
        const char = store.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }

    // Use a predefined color palette with lighter backgrounds
    const backgroundColors = [
        'rgba(25, 118, 210, 0.1)', 'rgba(56, 142, 60, 0.1)', 'rgba(245, 124, 0, 0.1)', 'rgba(123, 31, 162, 0.1)', 'rgba(211, 47, 47, 0.1)',
        'rgba(2, 136, 209, 0.1)', 'rgba(104, 159, 56, 0.1)', 'rgba(249, 168, 37, 0.1)', 'rgba(142, 36, 170, 0.1)', 'rgba(194, 24, 91, 0.1)',
        'rgba(255, 87, 34, 0.1)', 'rgba(0, 150, 136, 0.1)', 'rgba(63, 81, 181, 0.1)', 'rgba(233, 30, 99, 0.1)', 'rgba(0, 188, 212, 0.1)',
        'rgba(139, 195, 74, 0.1)', 'rgba(255, 193, 7, 0.1)', 'rgba(121, 85, 72, 0.1)', 'rgba(96, 125, 139, 0.1)', 'rgba(156, 39, 176, 0.1)'
    ];

    const borderColors = [
        'rgba(25, 118, 210, 0.3)', 'rgba(56, 142, 60, 0.3)', 'rgba(245, 124, 0, 0.3)', 'rgba(123, 31, 162, 0.3)', 'rgba(211, 47, 47, 0.3)',
        'rgba(2, 136, 209, 0.3)', 'rgba(104, 159, 56, 0.3)', 'rgba(249, 168, 37, 0.3)', 'rgba(142, 36, 170, 0.3)', 'rgba(194, 24, 91, 0.3)',
        'rgba(255, 87, 34, 0.3)', 'rgba(0, 150, 136, 0.3)', 'rgba(63, 81, 181, 0.3)', 'rgba(233, 30, 99, 0.3)', 'rgba(0, 188, 212, 0.3)',
        'rgba(139, 195, 74, 0.3)', 'rgba(255, 193, 7, 0.3)', 'rgba(121, 85, 72, 0.3)', 'rgba(96, 125, 139, 0.3)', 'rgba(156, 39, 176, 0.3)'
    ];

    // Use the hash to select colors, ensuring identical stores get the same styling
    const index = Math.abs(hash) % backgroundColors.length;
    return {
        backgroundColor: backgroundColors[index],
        border: `1px solid ${borderColors[index]}`,
        borderRadius: '8px',
        padding: '4px 8px',
        margin: '-2px -4px',
        display: 'inline-block',
        fontWeight: 'bold',
        boxShadow: `0 1px 3px ${borderColors[index]}20`
    };
};

// Memoized store background style cache
const storeStyleCache = new Map<string, { backgroundColor: string }>();

// Optimized helper function to get store background styling with caching
const getOptimizedStoreBackgroundStyle = (store: string) => {
    if (storeStyleCache.has(store)) {
        return storeStyleCache.get(store)!;
    }

    const style = getStoreBackgroundStyle(store);
    const optimizedStyle = { backgroundColor: style.backgroundColor };
    storeStyleCache.set(store, optimizedStyle);
    return optimizedStyle;
};

// Utility function to calculate store summary
const calculateStoreSummary = (records: any[]) => {
    const totalCube = records.reduce((sum, record) => sum + parseFloat(record.cube || 0), 0);
    const totalWeight = records.reduce((sum, record) => sum + parseFloat(record.weight || 0), 0);
    const uniqueRoutes = [...new Set(records.map((record: any) => record.route))].sort((a: string, b: string) => a.localeCompare(b));

    return {
        totalCube: totalCube.toFixed(2),
        totalWeight: totalWeight.toFixed(2),
        uniqueRoutes,
        routeCount: uniqueRoutes.length
    };
};

// Common styles for accordion components
const ACCORDION_STYLES = {
    container: {
        mb: 2,
        borderRadius: '12px !important',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        }
    },
    summary: {
        borderRadius: '12px',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        '& .MuiAccordionSummary-content': {
            alignItems: 'center',
        }
    },
    details: { p: 0 }
};

// Table styles
const TABLE_STYLES = {
    container: { boxShadow: 'none', border: 'none' },
    headerCell: {
        fontWeight: 'bold',
        fontSize: '0.75rem',
        fontFamily: FONT_FAMILY,
        color: '#666',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        py: 1
    },
    bodyCell: {
        fontSize: '0.85rem',
        fontFamily: FONT_FAMILY,
        border: 'none',
        py: 1.5
    }
};

// Route Types to icon mapping - easily configurable
const routeTypesIconMap: Record<string, React.ComponentType<any>> = {
    'Regular': Package,
    'Xdock': Building2,
    'Combo': Boxes,
    'Split': DoublePackageIcon,
    // Add more mappings as needed
};

interface DcHighlightDayProps extends PickersDayProps<Dayjs> {
    dcDate?: string;
}

interface DcClockSectionItemProps extends React.ComponentProps<typeof MenuItem> {
    dcHour12?: string;
    dcMinute?: string;
    dcMeridiem?: string;
}

const DcHighlightDay: React.FC<DcHighlightDayProps> = ({ dcDate, day, selected, ...other }) => {
    const isDcDate = Boolean(dcDate) && day.format('YYYY-MM-DD') === dcDate;

    return (
        <PickersDay
            {...other}
            day={day}
            selected={selected}
            disableHighlightToday
            sx={{
                ...(isDcDate
                    ? {
                        border: '2px solid #1976d2',
                        borderRadius: '50%'
                    }
                    : {})
            }}
        />
    );
};

const DcHighlightClockSectionItem: React.FC<DcClockSectionItemProps> = ({
    dcHour12,
    dcMinute,
    dcMeridiem,
    children,
    selected,
    sx,
    ...other
}) => {
    const label = String(children ?? '').trim();
    const ariaLabel = String(other['aria-label'] ?? '').toLowerCase();

    const isHourItem = ariaLabel.includes('hour');
    const isMinuteItem = ariaLabel.includes('minute');
    const labelUpper = label.toUpperCase();
    const normalizedMeridiemLabel = labelUpper.replace(/\./g, '').replace(/\s+/g, '');
    const isMeridiemItem =
        ariaLabel.includes('meridiem') ||
        normalizedMeridiemLabel === 'AM' ||
        normalizedMeridiemLabel === 'PM';

    const labelNumber = Number.parseInt(label, 10);
    const dcHourNumber = Number.parseInt(dcHour12 || '', 10);
    const dcMinuteNumber = Number.parseInt(dcMinute || '', 10);

    const hourCandidates = [dcHour12, String(Number(dcHour12 || ''))]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.trim());
    const minuteCandidates = [dcMinute, String(Number(dcMinute || ''))]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.trim());

    const isHourMatchByNumber = isHourItem && !Number.isNaN(labelNumber) && !Number.isNaN(dcHourNumber) && labelNumber === dcHourNumber;
    const isMinuteMatchByNumber = isMinuteItem && !Number.isNaN(labelNumber) && !Number.isNaN(dcMinuteNumber) && labelNumber === dcMinuteNumber;

    const isDcCurrentTimeOption =
        (isHourItem && hourCandidates.includes(label)) ||
        (isMinuteItem && minuteCandidates.includes(label)) ||
        isHourMatchByNumber ||
        isMinuteMatchByNumber ||
        (
            isMeridiemItem &&
            Boolean(dcMeridiem) &&
            normalizedMeridiemLabel === String(dcMeridiem).toUpperCase().replace(/\./g, '').replace(/\s+/g, '')
        );

    const itemRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!isDcCurrentTimeOption || !itemRef.current) {
            return;
        }

        requestAnimationFrame(() => {
            itemRef.current?.scrollIntoView({ block: 'start' });
        });
    }, [isDcCurrentTimeOption]);

    const highlightSx = isDcCurrentTimeOption
        ? {
            color: '#1565c0 !important',
            fontWeight: 700,
            border: '1px solid #64b5f6 !important',
            backgroundColor: '#e3f2fd !important',
            '&:hover': {
                backgroundColor: '#d6ebff !important'
            },
            '&.Mui-selected': {
                backgroundColor: '#bbdefb !important',
                color: '#0d47a1 !important'
            }
        }
        : undefined;

    return (
        <MenuItem
            {...other}
            ref={itemRef as any}
            selected={selected}
            sx={[sx as any, highlightSx]}
        >
            {children}
        </MenuItem>
    );
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`routing-tabpanel-${index}`}
            aria-labelledby={`routing-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `routing-tab-${index}`,
        'aria-controls': `routing-tabpanel-${index}`,
    };
}

// Reusable component for truncated text with tooltip
const TruncatedText: React.FC<{ text: string | null | undefined; maxWidth: string; tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right' }> = ({
    text,
    maxWidth,
    tooltipPlacement = 'top'
}) => {
    const normalizedText = (text ?? '')
        .replace(/[\r\n\u2028\u2029]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return (
    <Tooltip title={normalizedText} arrow placement={tooltipPlacement}>
        <Typography
            variant="body2"
            sx={{
                maxWidth,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                fontFamily: FONT_FAMILY
            }}
        >
            {normalizedText}
        </Typography>
    </Tooltip>
    );
};

const Routing: React.FC = () => {
    const { userPermissions } = useUserPermissions();
    const storedDC = sessionStorage.getItem('selectedDC') || "";
    const [selectedDC, setSelectedDC] = useState(storedDC);
    const getDcDateTimeSafely = useCallback((dcName?: string) => {
        if (!dcName) {
            return '';
        }

        try {
            return getCurrentDateForDC(dcName);
        } catch {
            return '';
        }
    }, []);

    const getDcDateSafely = useCallback((dcName?: string) => {
        const dcDateTime = getDcDateTimeSafely(dcName);
        return dcDateTime ? dcDateTime.split(' ')[0] : '';
    }, [getDcDateTimeSafely]);

    const activeDc = selectedDC || storedDC;

    const todayDate = useMemo(() => getDcDateSafely(activeDc), [activeDc, getDcDateSafely]);
    const [replaySuccessMessage, setReplaySuccessMessage] = useState<string | null>(null);
    const [replayErrorMessage, setReplayErrorMessage] = useState<string | null>(null);
    const [storeFilter, setStoreFilter] = useState('');
    const [fromDate, setFromDate] = useState(() => getDcDateSafely(storedDC));
    const [toDate, setToDate] = useState(() => getDcDateSafely(storedDC));

    // Ensure dates are always in DC timezone
    useEffect(() => {
        if (activeDc) {
            const dcTodayDate = getDcDateSafely(activeDc);
            setFromDate(dcTodayDate);
            setToDate(dcTodayDate);
        }
    }, [activeDc, getDcDateSafely]);
    const [hasAppliedSplitFilter, setHasAppliedSplitFilter] = useState(false);
    const [expandedDateGroups, setExpandedDateGroups] = useState<Set<string>>(new Set());
    const [expandedStoreGroups, setExpandedStoreGroups] = useState<Set<string>>(new Set());
    const [currentRouteStatus, setCurrentRouteStatus] = useState<string>('');
    const [dateFilterError, setDateFilterError] = useState<string | null>(null);

    // Local state for route filter inputs (separate from table state)
    const [routeFilterInputs, setRouteFilterInputs] = useState<Record<string, string>>({
        filename: '',
        route: '',
        status: ''
    });

    // Use the routing state hook
    const {
        value,
        loading,
        error,
        splitWorksheetLoading,
        splitWorksheetError,
        comboData,
        comboLoading,
        comboError,
        viewPayloadData,
        viewPayloadLoading,
        viewPayloadError,
        viewPayloadDialogOpen,
        setViewPayloadDialogOpen,
        currentRouteId,
        setCurrentRouteId,
        jsonSearchTerm,
        setJsonSearchTerm,
        replayData,
        replayLoading,
        replayError,
        replayConfirmDialogOpen,
        setReplayConfirmDialogOpen,
        pendingReplayRouteId,
        setPendingReplayRouteId,
        filters,
        dateFilters,
        page,
        setPage,
        rowsPerPage,
        setExpandedRows,
        expandedRows,
        paginatedData,
        filteredAndSortedSplitWorksheetData,
        totalPages,
        totalElements,
        startEntry,
        endEntry,
        handleChange,
        handleFilterChange,
        handleDateFilterChange,
        handleClearDateFilters,
        handleRowsPerPageChange,
        handlePageChange,
        handleToggleExpand,
        fetchRouteData,
        fetchSplitWorksheetData,
        fetchViewPayloadData,
        fetchReplayData
    } = useRoutingState(selectedDC);

    const currentFilenameFilter = filters.filename || '';
    const currentRouteFilter = filters.route || '';
    const dcNowDateTime = useMemo(() => getDcDateTimeSafely(activeDc), [activeDc, getDcDateTimeSafely]);
    const dcReferenceDateTime = useMemo(() => {
        if (!dcNowDateTime) {
            return dayjs();
        }

        return dayjs(dcNowDateTime.replace(' ', 'T'));
    }, [dcNowDateTime]);
    const dcCurrentHour12 = useMemo(() => dcReferenceDateTime.format('hh'), [dcReferenceDateTime]);
    const dcCurrentMinute = useMemo(() => dcReferenceDateTime.format('mm'), [dcReferenceDateTime]);
    const dcCurrentMeridiem = useMemo(() => dcReferenceDateTime.format('A'), [dcReferenceDateTime]);
    const dcReferenceDateOnly = useMemo(() => {
        if (!todayDate) {
            return dayjs();
        }

        return dayjs(todayDate);
    }, [todayDate]);

    // Only show DCs where the user has Routing capability
    const filteredGroups = useMemo(() => {
        if (!userPermissions) return [];
        const dcSet = new Set<string>();
        (userPermissions.roles || []).forEach(role => {
            if (role.dcCode && (role.capabilities || []).some(cap => cap.toLowerCase().includes('routing'))) {
                dcSet.add(role.dcCode);
            }
        });
        return Array.from(dcSet).sort();
    }, [userPermissions]);

    const filteredSplitWorksheetData = useMemo(() => {
        return filteredAndSortedSplitWorksheetData.filter(row => {
            // Support comma-separated store filter values
            let matchesStore = true;
            if (storeFilter) {
                const filterValues = storeFilter
                    .split(',')
                    .map(val => val.trim().toLowerCase())
                    .filter(val => val.length > 0);
                
                if (filterValues.length > 0) {
                    matchesStore = filterValues.some(filter => 
                        row.store.toLowerCase().includes(filter)
                    );
                }
            }

            // Show all data from API - no date filtering
            // Backend returns previous day, selected day, and next day data
            return matchesStore;
        });
    }, [filteredAndSortedSplitWorksheetData, storeFilter]);

    const handleApplyDateFilter = useCallback(() => {
        // Validate date range
        if (fromDate && toDate && fromDate > toDate) {
            setDateFilterError('From Date cannot be after To Date');
            return;
        }

        setDateFilterError(null);
        setHasAppliedSplitFilter(true);
        fetchSplitWorksheetData({
            fromDate: fromDate || undefined,
            toDate: toDate || undefined
        });
    }, [fetchSplitWorksheetData, fromDate, toDate]);

    const handleClearSplitDateFilter = useCallback(() => {
        setStoreFilter('');
        setFromDate(todayDate);
        setToDate(todayDate);
        setHasAppliedSplitFilter(true);
        setDateFilterError(null);
        setExpandedDateGroups(new Set());
        setExpandedStoreGroups(new Set());
        fetchSplitWorksheetData({
            fromDate: todayDate,
            toDate: todayDate
        });
    }, [fetchSplitWorksheetData, todayDate]);

    const groupedSplitWorksheetData = useMemo(() => {
        if (!hasAppliedSplitFilter) {
            return [];
        }

        const dateMap = new Map<string, any[]>();

        filteredSplitWorksheetData.forEach((record) => {
            const dateKey = getCalendarDateKey(record.createTs);
            if (!dateKey) {
                return;
            }

            const existing = dateMap.get(dateKey) || [];
            existing.push(record);
            dateMap.set(dateKey, existing);
        });

        return Array.from(dateMap.entries())
            .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
            .map(([dateKey, dateRecords]) => {
                const storeMap = new Map<string, any[]>();

                dateRecords.forEach((record) => {
                    const existing = storeMap.get(record.store) || [];
                    existing.push(record);
                    storeMap.set(record.store, existing);
                });

                const stores = Array.from(storeMap.entries())
                    .sort(([storeA], [storeB]) => storeA.localeCompare(storeB))
                    .map(([store, storeRecords]) => ({
                        storeKey: `${dateKey}::${store}`,
                        store,
                        records: [...storeRecords].sort((recordA, recordB) => {
                            const timeDifference = new Date(recordB.createTs).getTime() - new Date(recordA.createTs).getTime();
                            if (timeDifference !== 0) {
                                return timeDifference;
                            }

                            return recordA.route.localeCompare(recordB.route);
                        }),
                        summary: calculateStoreSummary(storeRecords)
                    }));

                return {
                    dateKey,
                    dateLabel: formatCalendarDate(dateKey),
                    stores,
                    totalShipments: stores.length,
                    uniqueRouteCount: new Set(dateRecords.map((record) => record.route)).size,
                    totalCube: dateRecords.reduce((sum, record) => sum + parseFloat(record.cube || 0), 0),
                    totalWeight: dateRecords.reduce((sum, record) => sum + parseFloat(record.weight || 0), 0)
                };
            });
    }, [filteredSplitWorksheetData, hasAppliedSplitFilter]);

    const handleDateAccordionToggle = useCallback((dateKey: string) => {
        setExpandedDateGroups(prev => {
            const next = new Set(prev);
            if (next.has(dateKey)) {
                next.delete(dateKey);
            } else {
                next.add(dateKey);
            }
            return next;
        });
    }, []);

    const handleStoreAccordionToggle = useCallback((storeKey: string) => {
        setExpandedStoreGroups(prev => {
            const next = new Set(prev);
            if (next.has(storeKey)) {
                next.delete(storeKey);
            } else {
                next.add(storeKey);
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (!hasAppliedSplitFilter || groupedSplitWorksheetData.length === 0) {
            setExpandedDateGroups(new Set());
            setExpandedStoreGroups(new Set());
            return;
        }

        setExpandedDateGroups(new Set([groupedSplitWorksheetData[0].dateKey]));
        setExpandedStoreGroups(new Set());
    }, [groupedSplitWorksheetData, hasAppliedSplitFilter]);

    // Auto-fetch split worksheet with today's date when switching to that tab
    useEffect(() => {
        if (value === SPLIT_WORKSHEET_TAB_INDEX) {
            const dcTodayDate = getDcDateSafely(selectedDC || storedDC);
            setFromDate(dcTodayDate);
            setToDate(dcTodayDate);
            setHasAppliedSplitFilter(true);
            setDateFilterError(null);
            fetchSplitWorksheetData({
                fromDate: dcTodayDate,
                toDate: dcTodayDate
            });
        }
    }, [value, selectedDC, storedDC, getDcDateSafely, fetchSplitWorksheetData]);

    // Check if user has routing access for the currently selected DC
    const hasCamsRoutingAccess = useMemo(() => {
        if (!userPermissions || !selectedDC) return false;
        return userPermissions.roles
            .filter(role => role.dcCode === selectedDC)
            .some(role =>
                (role.capabilities || []).some(cap => cap.toLowerCase().includes('routing'))
            );
    }, [userPermissions, selectedDC]);

    // Manage selectedDC state and sessionStorage
    useEffect(() => {
        if (filteredGroups.length === 0) return;
        if (!selectedDC) {
            const validStored = storedDC && filteredGroups.includes(storedDC) ? storedDC : filteredGroups[0];
            setSelectedDC(validStored);
            sessionStorage.setItem('selectedDC', validStored);
            broadcastDCChange(validStored);
        } else if (!filteredGroups.includes(selectedDC)) {
            setSelectedDC(filteredGroups[0]);
            sessionStorage.setItem('selectedDC', filteredGroups[0]);
            broadcastDCChange(filteredGroups[0]);
        } else {
            sessionStorage.setItem('selectedDC', selectedDC);
            broadcastDCChange(selectedDC);
        }
    }, [filteredGroups, selectedDC, storedDC]);

    const handleDCChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newDC = event.target.value;
        const dcTodayDate = getDcDateSafely(newDC);

        if (newDC === selectedDC) {
            return;
        }

        setSelectedDC(newDC);

        // Reset split worksheet filters when DC changes.
        setStoreFilter('');
        setFromDate(dcTodayDate);
        setToDate(dcTodayDate);
        setHasAppliedSplitFilter(true);
        setDateFilterError(null);
        setExpandedDateGroups(new Set());
        setExpandedStoreGroups(new Set());

        // Reset Route Details filters when DC changes.
        setRouteFilterInputs({ filename: '', route: '', status: '' });
        if (currentFilenameFilter) {
            handleFilterChange('filename', '');
        }
        if (currentRouteFilter) {
            handleFilterChange('route', '');
        }
        if ((filters.status || '').trim() !== '') {
            handleFilterChange('status', '');
        }
        if (dateFilters.from || dateFilters.to) {
            handleClearDateFilters();
        }
        setExpandedRows(new Set());

        if (value === SPLIT_WORKSHEET_TAB_INDEX) {
            fetchSplitWorksheetData({
                dcName: newDC,
                fromDate: dcTodayDate,
                toDate: dcTodayDate
            });
        }
    };

    // Clear all Route Details filters at once
    const handleClearAllFilters = useCallback(() => {
        setRouteFilterInputs({ filename: '', route: '', status: '' });
        handleFilterChange('filename', '');
        handleFilterChange('route', '');
        handleFilterChange('status', '');
        handleClearDateFilters();
        setExpandedRows(new Set());
    }, [handleFilterChange, handleClearDateFilters, setExpandedRows]);

    // Local filter change handler that updates input state immediately
    const handleRouteFilterChange = useCallback((column: string, value: string) => {
        setRouteFilterInputs(prev => ({
            ...prev,
            [column]: value
        }));
    }, []);

    // Sync input values when filters change externally (e.g., programmatic clearing)
    useEffect(() => {
        setRouteFilterInputs(prev => ({
            ...prev,
            filename: currentFilenameFilter,
            route: currentRouteFilter
        }));
    }, [currentFilenameFilter, currentRouteFilter]);

    // Debounced effect for route filter input changes - sync to actual filters
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            // Only update filters if the input values have actually changed
            const hasChanges = Object.entries(routeFilterInputs).some(([key, value]) => {
                return ((key === 'filename' ? currentFilenameFilter : currentRouteFilter) ?? '') !== value;
            });

            if (hasChanges) {
                Object.entries(routeFilterInputs).forEach(([key, value]) => {
                    if (((key === 'filename' ? currentFilenameFilter : currentRouteFilter) ?? '') !== value) {
                        handleFilterChange(key, value);
                    }
                });
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [routeFilterInputs, currentFilenameFilter, currentRouteFilter, handleFilterChange]);

    // Handle replay confirmation
    const handleReplayConfirm = useCallback((routeId: number) => {
        setPendingReplayRouteId(routeId);
        setReplayConfirmDialogOpen(true);
    }, [setPendingReplayRouteId, setReplayConfirmDialogOpen]);

    const handleReplayConfirmContinue = useCallback(() => {
        if (pendingReplayRouteId !== null) {
            fetchReplayData(pendingReplayRouteId);
        }
        // Don't close dialog here - let it close after API completes
    }, [pendingReplayRouteId, fetchReplayData]);

    const handleReplayConfirmCancel = useCallback(() => {
        setReplayConfirmDialogOpen(false);
        setPendingReplayRouteId(null);
    }, [setPendingReplayRouteId, setReplayConfirmDialogOpen]);

    // Close replay dialog after API completes
    useEffect(() => {
        if (replayData || replayError) {
            setReplayConfirmDialogOpen(false);
            setPendingReplayRouteId(null);
        }
    }, [replayData, replayError, setReplayConfirmDialogOpen, setPendingReplayRouteId]);

    // Handle replay completion - show success/error message and refresh data
    const prevReplayLoadingRef = useRef(replayLoading);
    useEffect(() => {
        const prevLoading = prevReplayLoadingRef.current;
        prevReplayLoadingRef.current = replayLoading;

        // Check if replay just completed (loading changed from true to false)
        if (prevLoading && !replayLoading) {
            if (!replayError) {
                // Show success message
                setReplaySuccessMessage("Route replay completed successfully!");
                setReplayErrorMessage(null); // Clear any previous error message
                
                // Close the view payload dialog if it's open
                setViewPayloadDialogOpen(false);
                
                // Refresh the data similar to initial load
                if (selectedDC) {
                    fetchRouteData();
                }
            } else {
                // Show error message
                setReplayErrorMessage(`Route replay failed: ${replayError}`);
                setReplaySuccessMessage(null); // Clear any previous success message
            }
        }
    }, [replayLoading, replayError, selectedDC, fetchRouteData, setViewPayloadDialogOpen]);

    // Helper function to highlight search terms in JSON
    const highlightSearchTerm = useCallback((text: string, searchTerm: string) => {
        if (!searchTerm.trim()) return text;

        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span key={index} style={{
                    backgroundColor: '#ffeb3b',
                    color: '#000',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontWeight: 'bold'
                }}>
                    {part}
                </span>
            ) : part
        );
    }, []);

    // Memoized highlighted JSON
    const highlightedJson = useMemo(() => {
        if (!viewPayloadData) return null;
        return highlightSearchTerm(JSON.stringify(viewPayloadData, null, 2), jsonSearchTerm);
    }, [viewPayloadData, jsonSearchTerm, highlightSearchTerm]);

    // Reset page to 0 if current page exceeds available pages after filtering
    useEffect(() => {
        if (page >= totalPages && totalPages > 0) {
            setPage(0);
        }
    }, [page, totalPages, setPage]);

    const getStatusColor = useCallback((status: string) => {
        switch (status.toLowerCase()) {
            case 'success':
                return 'success';
            case 'in progress':
                return 'warning';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    }, []);

    // Helper function to get status dot color
    const getStatusDotColor = useCallback((status: string) => {
        const statusColor = getStatusColor(status);
        switch (statusColor) {
            case 'success': return '#4caf50';
            case 'warning': return '#ffb74d';
            case 'error': return '#f44336';
            default: return '#2196f3'; // parsed
        }
    }, [getStatusColor]);

    // Helper function to get Chip styles for status
    const getStatusChipStyles = useCallback((status: string) => {
        const baseStyles = {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            textTransform: 'uppercase',
            borderRadius: '6px',
            minHeight: 28,
            width: '100px',
            padding: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            fontSize: '0.68rem',
            fontWeight: 'bold',
            letterSpacing: '0.6px',
            fontFamily: FONT_FAMILY,
            border: '1px solid rgba(0,0,0,0.06)',
            '& .MuiChip-label': {
                padding: '2px 10px',
            }
        };

        const statusColor = getStatusColor(status);
        const colorStyles = {
            success: {
                backgroundColor: '#4caf50',
                color: '#ffffff',
                border: '1px solid rgba(76, 175, 80, 0.18)'
            },
            warning: {
                backgroundColor: '#ffb74d',
                color: '#000000',
                border: '1px solid rgba(255,183,77,0.18)'
            },
            error: {
                backgroundColor: '#f44336',
                color: '#ffffff',
                border: '1px solid rgba(244,67,54,0.18)'
            },
            default: { // parsed
                backgroundColor: '#2196f3',
                color: '#ffffff',
                border: '1px solid rgba(33,150,243,0.18)'
            }
        };

        return { ...baseStyles, ...colorStyles[statusColor] };
    }, [getStatusColor]);

    return (
        <>
            <Box sx={{ width: '100%' }}>
            <div className="routing-title">
                <p style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>Routing</p>
                <div className="routing-dc-dropdown">
                    <select
                        value={selectedDC}
                        onChange={handleDCChange}
                        disabled={!userPermissions}
                        className={`customSelect ${!userPermissions ? 'disabled' : 'enabled'}`}
                    >
                        <option value="">Select DC</option>
                        {filteredGroups.map((dc: string) => (
                            <option key={dc} value={dc}>{dc}</option>
                        ))}
                    </select>
                </div>
            </div>
            {/* Check if user has access to routing for the selected DC */}
            {
            !hasCamsRoutingAccess ? (
                <div className='under-construction'>You don't have access to this operation for the selected DC.</div>
            ) : 
                (<React.Fragment>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tabs value={value} onChange={handleChange} aria-label="routing tabs">
                        <Tab label="Route Details" {...a11yProps(ROUTE_DETAILS_TAB_INDEX)} sx={{ fontFamily: FONT_FAMILY }} />
                        <Tab label="Split Worksheet" {...a11yProps(SPLIT_WORKSHEET_TAB_INDEX)} sx={{ fontFamily: FONT_FAMILY }} />
                    </Tabs>

                    {/* Indicator for past data highlighting - Route Details only */}
                    {value === ROUTE_DETAILS_TAB_INDEX && (
                    <Alert severity="info" sx={{ mb: 0, mt: 0, mr: 2, py: 0.5, px: 2 }}>
                        <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>
                            <strong>Past Data:</strong> Yellow highlighted records
                        </Typography>
                    </Alert>
                    )}
                </Box>
            </Box>
            <TabPanel value={value} index={ROUTE_DETAILS_TAB_INDEX}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<X size={14} />}
                        onClick={handleClearAllFilters}
                        disabled={loading || !selectedDC}
                        sx={{
                            fontFamily: FONT_FAMILY,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Clear Filters
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshCw size={16} />}
                        onClick={fetchRouteData}
                        disabled={loading || !selectedDC}
                        sx={{
                            fontFamily: FONT_FAMILY,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Refresh
                    </Button>
                </Box>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {replaySuccessMessage && (
                    <Alert 
                        severity="success" 
                        sx={{ mb: 2 }}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setReplaySuccessMessage(null)}
                            >
                                <X size={18} />
                            </IconButton>
                        }
                    >
                        {replaySuccessMessage}
                    </Alert>
                )}
                {replayErrorMessage && (
                    <Alert 
                        severity="error" 
                        sx={{ mb: 2 }}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setReplayErrorMessage(null)}
                            >
                                <X size={18} />
                            </IconButton>
                        }
                    >
                        {replayErrorMessage}
                    </Alert>
                )}
                <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto', margin:0 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {['route', 'filename', 'date', 'status', 'routeTypes', 'remark', 'actions'].map((column) => {
                                    const cellWidth = COLUMN_WIDTHS[column as keyof typeof COLUMN_WIDTHS] || '';

                                    if (column === 'date') {
                                        return (
                                            <TableCell key={column} sx={{ fontWeight: 'bold', borderBottom: '2px solid #ddd', fontSize: '0.8rem', fontFamily: FONT_FAMILY, width: cellWidth, position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                                <Box sx={{ mb: 1 }}>
                                                    <span style={{ textTransform: 'capitalize', fontFamily: FONT_FAMILY }}>
                                                        {column.replace(/([A-Z])/g, ' $1').trim()}
                                                    </span>
                                                </Box>
                                                <LocalizationProvider
                                                    dateAdapter={AdapterDayjs}
                                                    localeText={{
                                                        fieldYearPlaceholder: () => 'YYYY',
                                                        fieldMonthPlaceholder: () => 'MM',
                                                        fieldDayPlaceholder: () => 'DD',
                                                        fieldHoursPlaceholder: () => 'HH',
                                                        fieldMinutesPlaceholder: () => 'MM',
                                                        fieldMeridiemPlaceholder: () => 'AM/PM'
                                                    }}
                                                >
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.14, width: '100%', background: 'linear-gradient(135deg, #eef6ff 0%, #f9fcff 62%, #eef7ff 100%)', borderRadius: '10px', px: 0.16, py: 0.14, border: '1px solid #cfe3f9', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 4px rgba(25,118,210,0.08)' }}>
                                                    <DesktopDateTimePicker
                                                        format="YYYY-MM-DD hh:mm A"
                                                        timeSteps={{ minutes: 1 }}
                                                        slots={{ day: DcHighlightDay, digitalClockSectionItem: DcHighlightClockSectionItem }}
                                                        referenceDate={dcReferenceDateTime}
                                                        defaultValue={dcReferenceDateTime}
                                                        value={dateFilters.from ? dayjs(dateFilters.from) : null}
                                                        onChange={(newValue: Dayjs | null) => {
                                                            if (newValue) {
                                                                handleDateFilterChange('from', newValue.format('YYYY-MM-DDTHH:mm'));
                                                            }
                                                        }}
                                                        disabled={loading}
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                placeholder: 'YYYY-MM-DD HH:MM AM/PM',
                                                                sx: DATE_FILTER_FIELD_SX
                                                            },
                                                            day: { dcDate: todayDate } as any,
                                                            digitalClockSectionItem: {
                                                                dcHour12: dcCurrentHour12,
                                                                dcMinute: dcCurrentMinute,
                                                                dcMeridiem: dcCurrentMeridiem
                                                            } as any,
                                                            desktopPaper: { sx: DATE_PICKER_PAPER_SX }
                                                        }}
                                                    />
                                                    <Typography variant="body2" sx={{ fontSize: '0.6rem', color: '#2a6fb1', fontFamily: FONT_FAMILY, fontWeight: 800, mx: 0.04, backgroundColor: 'rgba(42, 111, 177, 0.12)', borderRadius: '999px', px: 0.32, lineHeight: 1.2 }}>
                                                        →
                                                    </Typography>
                                                    <DesktopDateTimePicker
                                                        format="YYYY-MM-DD hh:mm A"
                                                        timeSteps={{ minutes: 1 }}
                                                        slots={{ day: DcHighlightDay, digitalClockSectionItem: DcHighlightClockSectionItem }}
                                                        referenceDate={dcReferenceDateTime}
                                                        defaultValue={dcReferenceDateTime}
                                                        value={dateFilters.to ? dayjs(dateFilters.to) : null}
                                                        onChange={(newValue: Dayjs | null) => {
                                                            if (newValue) {
                                                                handleDateFilterChange('to', newValue.format('YYYY-MM-DDTHH:mm'));
                                                            }
                                                        }}
                                                        disabled={loading}
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                placeholder: 'YYYY-MM-DD HH:MM AM/PM',
                                                                sx: DATE_FILTER_FIELD_SX
                                                            },
                                                            day: { dcDate: todayDate } as any,
                                                            digitalClockSectionItem: {
                                                                dcHour12: dcCurrentHour12,
                                                                dcMinute: dcCurrentMinute,
                                                                dcMeridiem: dcCurrentMeridiem
                                                            } as any,
                                                            desktopPaper: { sx: DATE_PICKER_PAPER_SX }
                                                        }}
                                                    />
                                                </Box>
                                                </LocalizationProvider>
                                            </TableCell>
                                        );
                                    }

                                    return (
                                        <TableCell
                                            key={column}
                                            sx={{
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid #ddd',
                                                fontSize: '0.8rem',
                                                fontFamily: FONT_FAMILY,
                                                width: cellWidth,
                                                position: 'sticky',
                                                top: 0,
                                                backgroundColor: '#fff',
                                                zIndex: 1
                                            }}
                                        >
                                            <Box>
                                                <span style={{ textTransform: 'capitalize', fontFamily: FONT_FAMILY }}>
                                                    {column.replace(/([A-Z])/g, ' $1').trim()}
                                                </span>
                                            </Box>
                                            {column === 'status' ? (
                                                <Select
                                                    size="small"
                                                    value={routeFilterInputs[column] || ''}
                                                    onChange={(e: SelectChangeEvent<string>) => handleRouteFilterChange(column, e.target.value)}
                                                    disabled={loading}
                                                    displayEmpty
                                                    sx={{
                                                        mt: 1,
                                                        width: '100%',
                                                        '& .MuiSelect-select': {
                                                            fontSize: '0.7rem',
                                                            height: '28px',
                                                            fontFamily: FONT_FAMILY,
                                                            padding: '4px 8px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }
                                                    }}
                                                    renderValue={(selected) => {
                                                        if (!selected) {
                                                            return <em style={{ fontFamily: FONT_FAMILY, fontSize: '0.7rem' }}>All Statuses</em>;
                                                        }
                                                        return (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getStatusDotColor(selected) }} />
                                                                {selected.toUpperCase()}
                                                            </Box>
                                                        );
                                                    }}
                                                >
                                                    <MenuItem value="" sx={{ fontFamily: FONT_FAMILY, fontSize: '0.7rem' }}>
                                                        <em>All Statuses</em>
                                                    </MenuItem>
                                                    {STATUS_OPTIONS.map((option) => (
                                                        <MenuItem key={option.value} value={option.value} sx={{ fontFamily: FONT_FAMILY, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: option.color }} />
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            ) : column !== 'routeTypes' && column !== 'remark' && column !== 'actions' && (
                                                <TextField
                                                    size="small"
                                                    placeholder={`Filter ${column}`}
                                                    value={routeFilterInputs[column] || ''}
                                                    onChange={(e) => handleRouteFilterChange(column, e.target.value)}
                                                    disabled={loading}
                                                    sx={COMMON_STYLES.filterTextField}
                                                />
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        {loading && (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                        {!loading && (
                            <TableBody>
                            {paginatedData.map((row) => (
                                <React.Fragment key={row.id}>
                                    <TableRow
                                        hover
                                        sx={{
                                            '& > *': { padding: '4px 10px' },
                                            backgroundColor: !!row.isPastData ? '#ffecb3' : 'transparent'
                                        }}
                                    >
                                    <TableCell sx={{ ...COMMON_STYLES.tableCell, width: COLUMN_WIDTHS.route }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggleExpand(row.id)}
                                                sx={{ p: 0.5 }}
                                            >
                                                {expandedRows.has(row.id) ? <Minus size={16} /> : <Plus size={16} />}
                                            </IconButton>
                                            {row.routeId.toUpperCase()}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...COMMON_STYLES.tableCell, width: COLUMN_WIDTHS.filename }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                fontFamily: FONT_FAMILY
                                            }}
                                        >
                                            {(row.fileNm ?? '').replace(/[\r\n\u2028\u2029]+/g, ' ').replace(/\s+/g, ' ').trim()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...COMMON_STYLES.tableCell, width: COLUMN_WIDTHS.date }}>{formatDate(row.createTs)}</TableCell>
                                    <TableCell sx={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', border: '1px solid #e0e0e0', width: COLUMN_WIDTHS.status }}>
                                        <Chip
                                            label={row.status}
                                            color={getStatusColor(row.status)}
                                            size="small"
                                            sx={getStatusChipStyles(row.status)}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...COMMON_STYLES.tableCell, width: COLUMN_WIDTHS.routeTypes }}>
                                        <Box className="route-icons-container">
                                            {(() => {
                                                const routeTypeChecks = {
                                                    'Xdock': isRouteFlagEnabled(row.xdockFlag),
                                                    'Combo': isRouteFlagEnabled(row.comboFlag),
                                                    'Split': isRouteFlagEnabled(row.splitFlag),
                                                    'Regular': !isRouteFlagEnabled(row.xdockFlag) && !isRouteFlagEnabled(row.comboFlag) && !isRouteFlagEnabled(row.splitFlag)
                                                };

                                                return ['Regular', 'Xdock', 'Combo', 'Split'].map((routeType) => {
                                                    const hasRouteType = routeTypeChecks[routeType as keyof typeof routeTypeChecks];
                                                    const IconComponent = routeTypesIconMap[routeType];

                                                    return (
                                                        <div key={routeType} className="route-icon-wrapper">
                                                            {hasRouteType && IconComponent ? (
                                                                <Tooltip title={routeType} arrow placement="top">
                                                                    <IconComponent className="route-icon route-icon-regular" size={18} />
                                                                </Tooltip>
                                                            ) : null}
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...COMMON_STYLES.tableCell, width: COLUMN_WIDTHS.remark }}>
                                        <TruncatedText text={row.remarks} maxWidth="140px" />
                                    </TableCell>
                                    <TableCell sx={{ ...COMMON_STYLES.tableCell, width: COLUMN_WIDTHS.actions }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<Eye size={16} />}
                                                onClick={() => {
                                                    fetchViewPayloadData(row.id, row.id.toString());
                                                    setCurrentRouteStatus(row.status);
                                                }}
                                                sx={{
                                                    ...COMMON_STYLES.smallButton,
                                                    backgroundColor: '#f5f5f5',
                                                    color: '#424242',
                                                    '&:hover': {
                                                        backgroundColor: '#e0e0e0',
                                                        color: '#212121'
                                                    }
                                                }}
                                            >
                                                View
                                            </Button>
                                            <Tooltip
                                                title={
                                                    (row.status?.toLowerCase() === 'in progress' || row.status?.toLowerCase() === 'failed')
                                                        ? `Replay not available for ${row.status} status`
                                                        : row.replayCount > 0
                                                            ? `Replayed ${row.replayCount} time${row.replayCount > 1 ? 's' : ''}`
                                                            : 'Ready to replay'
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<RefreshCw size={16} />}
                                                    onClick={() => handleReplayConfirm(row.id)}
                                                    disabled={row.status?.toLowerCase() === 'in progress' || row.status?.toLowerCase() === 'failed'}
                                                    sx={{
                                                        ...COMMON_STYLES.smallButton,
                                                        ...(row.replayCount === 0 ? {
                                                            backgroundColor: '#4caf50',
                                                            color: '#ffffff',
                                                            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                                            '&:hover': {
                                                                backgroundColor: '#45a049',
                                                                color: '#ffffff',
                                                                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                                                                transform: 'translateY(-1px)'
                                                            }
                                                        } : {
                                                            backgroundColor: '#e3f2fd',
                                                            color: '#1976d2',
                                                            boxShadow: '0 2px 8px rgba(0, 118, 210, 0.3)',
                                                            '&:hover': {
                                                                backgroundColor: '#bbdefb',
                                                                color: '#1565c0',
                                                                boxShadow: '0 4px 12px rgba(0, 118, 210, 0.4)',
                                                                transform: 'translateY(-1px)'
                                                            }
                                                        }),
                                                        borderRadius: '8px',
                                                        transition: 'all 0.3s ease',
                                                        position: 'relative',
                                                        ...(row.replayCount > 0 && {
                                                            '&::after': {
                                                                content: `"${row.replayCount}"`,
                                                                position: 'absolute',
                                                                top: '-8px',
                                                                right: '-8px',
                                                                background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                                                                color: 'white',
                                                                borderRadius: '50%',
                                                                width: '20px',
                                                                height: '20px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.65rem',
                                                                fontWeight: 'bold',
                                                                boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)',
                                                                border: '2px solid white',
                                                                animation: 'bounce 2s infinite'
                                                            },
                                                            '@keyframes bounce': {
                                                                '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                                                                '40%': { transform: 'translateY(-3px)' },
                                                                '60%': { transform: 'translateY(-2px)' }
                                                            }
                                                        }),
                                                        '&:disabled': {
                                                            backgroundColor: '#e0e0e0',
                                                            color: '#9e9e9e',
                                                            boxShadow: 'none',
                                                            transform: 'none',
                                                            cursor: 'not-allowed',
                                                            '&:hover': {
                                                                backgroundColor: '#e0e0e0',
                                                                color: '#9e9e9e',
                                                                boxShadow: 'none',
                                                                transform: 'none'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    Replay
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                {expandedRows.has(row.id) && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ padding: 0, border: 'none' }}>
                                            <Box sx={{ p: 2, border: '1px solid #e0e0e0', mt: 1 }}>
                                                {comboLoading[row.id] && <CircularProgress size={20} />}
                                                {comboError[row.id] && <Alert severity="error">{comboError[row.id]}</Alert>}
                                                {!comboLoading[row.id] && !comboError[row.id] && (
                                                    <Box>
                                                        {!comboData[row.id] || Object.keys(comboData[row.id]).length === 0 || Object.values(comboData[row.id]).every(routes => routes.length === 0) ? (
                                                            <Typography variant="body1" sx={{ fontFamily: FONT_FAMILY, textAlign: 'center', py: 2 }}>
                                                                No combo routes identified
                                                            </Typography>
                                                        ) : (
                                                            Object.entries(comboData[row.id])
                                                                .filter(([key, routes]) => routes.length > 0)
                                                                .map(([key, routes]) => (
                                                                    <Box key={key} sx={{ mb: 3 }}>
                                                                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Stop</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Sequence</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Commodity Type</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Route Group</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Weight</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Cube</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Arrival Time</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold', fontFamily: FONT_FAMILY }}>Departure Time</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {routes.map((route, index) => (
                                                                                        <TableRow key={index}>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.9 }}>
                                                                                                    <span>{route.stop || '-'}</span>
                                                                                                    {isIndicatorEnabled(route.splitIndicator) && (
                                                                                                        <Tooltip title="Split" arrow placement="top">
                                                                                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                                                                <DoublePackageIcon className="route-icon route-icon-regular" size={14} />
                                                                                                            </Box>
                                                                                                        </Tooltip>
                                                                                                    )}
                                                                                                </Box>
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{route.sequence ?? '-'}</TableCell>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{route.productTypeCd || '-'}</TableCell>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{route.routeGroupId ?? '-'}</TableCell>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{route.weight ?? '-'}</TableCell>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{route.cube ?? '-'}</TableCell>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{route.arrivalTs || '-'}</TableCell>
                                                                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{route.departureTs || '-'}</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </TableContainer>
                                                                    </Box>
                                                                ))
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                                </React.Fragment>
                            ))}
                            {paginatedData.length === 0 && (
                                <TableRow>
                                    <TableCell 
                                        colSpan={6} 
                                        sx={{ 
                                            textAlign: 'center', 
                                            py: 6,
                                            px: 4,
                                            border: 'none'
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 2,
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            borderRadius: '16px',
                                            padding: '32px 24px',
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            maxWidth: '350px',
                                            margin: '0 auto'
                                        }}>
                                            <Typography variant="h5" sx={{
                                                fontFamily: FONT_FAMILY,
                                                fontWeight: 'bold',
                                                color: '#1976d2',
                                                mb: 1,
                                                fontSize: '1.6rem',
                                                textAlign: 'center'
                                            }}>
                                                No Data Available
                                            </Typography>
                                            <Typography variant="body1" sx={{
                                                fontFamily: FONT_FAMILY,
                                                color: '#666',
                                                fontSize: '1rem',
                                                lineHeight: 1.5,
                                                textAlign: 'center',
                                                maxWidth: '280px'
                                            }}>
                                                There are currently no routing records to display.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        )}
                    </Table>
                </TableContainer>

                {/* Fancy Pagination Section - Only show if there are records */}
                {totalElements > 0 && (
                <Box sx={{
                    mt: 1,
                    mx: 1,
                    p: 1,
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        {/* Left Section - Entry Info & Rows Per Page */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                background: 'rgba(255, 255, 255, 0.8)',
                                padding: '8px 16px',
                                borderRadius: '12px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                            }}>
                                <List size={16} style={{ color: '#1976d2' }} />
                                <Typography variant="body2" sx={{
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 'bold',
                                    color: '#1976d2',
                                    fontSize: '0.85rem'
                                }}>
                                    {startEntry} - {endEntry} of {totalElements} entries
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" sx={{
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: '600',
                                    color: '#495057',
                                    fontSize: '0.85rem'
                                }}>
                                    Show:
                                </Typography>
                                <Select
                                    value={rowsPerPage}
                                    onChange={(e: SelectChangeEvent<number>) => handleRowsPerPageChange(Number(e.target.value))}
                                    size="small"
                                    sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: '0.8rem',
                                        minWidth: '90px',
                                        height: '36px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        borderRadius: '10px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(25, 118, 210, 0.2)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                            borderWidth: '2px',
                                        }
                                    }}
                                >
                                    <MenuItem value={25} sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        color: '#1976d2',
                                        borderRadius: '6px',
                                        mx: 1,
                                        my: 0.5
                                    }}>
                                        25
                                    </MenuItem>
                                    <MenuItem value={50} sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        color: '#1976d2',
                                        borderRadius: '6px',
                                        mx: 1,
                                        my: 0.5
                                    }}>
                                        50
                                    </MenuItem>
                                    <MenuItem value={75} sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        color: '#1976d2',
                                        borderRadius: '6px',
                                        mx: 1,
                                        my: 0.5
                                    }}>
                                        75
                                    </MenuItem>
                                    <MenuItem value={100} sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        color: '#1976d2',
                                        borderRadius: '6px',
                                        mx: 1,
                                        my: 0.5
                                    }}>
                                        100
                                    </MenuItem>
                                </Select>
                            </Box>
                        </Box>

                        {/* Right Section - Navigation */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handlePageChange(0)}
                                disabled={page === 0}
                                startIcon={<ChevronsLeft size={16} />}
                                sx={{
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 'bold',
                                    borderRadius: '12px',
                                    px: 2,
                                    py: 1,
                                    textTransform: 'none',
                                    background: page === 0
                                        ? '#e0e0e0'
                                        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                    color: page === 0 ? '#999' : '#ffffff',
                                    boxShadow: page === 0
                                        ? 'none'
                                        : '0 4px 15px rgba(25, 118, 210, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': page === 0 ? {} : {
                                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                        transform: 'translateY(-2px)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#999'
                                    }
                                }}
                            >
                                First
                            </Button>

                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 0}
                                startIcon={<ChevronLeft size={16} />}
                                sx={{
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 'bold',
                                    borderRadius: '12px',
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    background: page === 0
                                        ? '#e0e0e0'
                                        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                    color: page === 0 ? '#999' : '#ffffff',
                                    boxShadow: page === 0
                                        ? 'none'
                                        : '0 4px 15px rgba(25, 118, 210, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': page === 0 ? {} : {
                                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                        transform: 'translateY(-2px)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#999'
                                    }
                                }}
                            >
                                Previous
                            </Button>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'rgba(255, 255, 255, 0.9)',
                                padding: '10px 20px',
                                borderRadius: '12px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                minWidth: '120px',
                                justifyContent: 'center'
                            }}>
                                <Typography variant="body2" sx={{
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 'bold',
                                    color: '#1976d2',
                                    fontSize: '0.9rem'
                                }}>
                                    {page + 1} / {totalPages}
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= totalPages - 1}
                                endIcon={<ChevronRight size={16} />}
                                sx={{
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 'bold',
                                    borderRadius: '12px',
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    background: page >= totalPages - 1
                                        ? '#e0e0e0'
                                        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                    color: page >= totalPages - 1 ? '#999' : '#ffffff',
                                    boxShadow: page >= totalPages - 1
                                        ? 'none'
                                        : '0 4px 15px rgba(25, 118, 210, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': page >= totalPages - 1 ? {} : {
                                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                        transform: 'translateY(-2px)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#999'
                                    }
                                }}
                            >
                                Next
                            </Button>

                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={page >= totalPages - 1}
                                endIcon={<ChevronsRight size={16} />}
                                sx={{
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 'bold',
                                    borderRadius: '12px',
                                    px: 2,
                                    py: 1,
                                    textTransform: 'none',
                                    background: page >= totalPages - 1
                                        ? '#e0e0e0'
                                        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                    color: page >= totalPages - 1 ? '#999' : '#ffffff',
                                    boxShadow: page >= totalPages - 1
                                        ? 'none'
                                        : '0 4px 15px rgba(25, 118, 210, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': page >= totalPages - 1 ? {} : {
                                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                        transform: 'translateY(-2px)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#999'
                                    }
                                }}
                            >
                                Last
                            </Button>
                        </Box>
                    </Box>
                </Box>
                )}

                {/* Expanded Route Details - moved inside table for better visibility */}
                {/* This section is now handled within the TableBody */}
            </TabPanel>
            <TabPanel value={value} index={SPLIT_WORKSHEET_TAB_INDEX}>
                {splitWorksheetError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {splitWorksheetError}
                    </Alert>
                )}

                {/* Grouped Store Layout - Same Stores Grouped Together */}
                <Box sx={{ p: 2 }}>
                    {/* Filter Controls */}
                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Filter stores (e.g., 240124,2324)"
                            value={storeFilter}
                            onChange={(e) => setStoreFilter(e.target.value)}
                            InputProps={{
                                startAdornment: <Search size={18} style={{ marginRight: 8, color: '#666' }} />,
                            }}
                            title="Use comma to search multiple stores (e.g., 240124,2324)"
                            sx={{
                                width: '320px',
                                '& .MuiInputBase-root': {
                                    fontSize: '0.95rem',
                                    height: '48px',
                                    fontFamily: FONT_FAMILY,
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(25, 118, 210, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#1976d2',
                                        borderWidth: '2px',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#1976d2',
                                        borderWidth: '2px',
                                        boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)',
                                    },
                                },
                            }}
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="From Date"
                            format="YYYY-MM-DD"
                            referenceDate={dcReferenceDateOnly}
                            value={
                                fromDate
                                    ? dayjs(fromDate)
                                    : null
                            }
                            onChange={(newValue: Dayjs | null) => {
                                const dateValue = newValue ? newValue.format('YYYY-MM-DD') : '';
                                setFromDate(dateValue);
                                if (dateFilterError) setDateFilterError(null);
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    placeholder: 'YYYY-MM-DD',
                                    title: 'Date in DC timezone',
                                    sx: {
                                        width: '170px',
                                        '& .MuiInputBase-root': {
                                            fontSize: '0.85rem',
                                            height: '48px',
                                            fontFamily: FONT_FAMILY,
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)'
                                        }
                                    }
                                },
                                day: (ownerState: any) => ({
                                    sx: ownerState.today ? {
                                        border: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        color: 'inherit !important'
                                    } : {}
                                })
                            }}
                        />
                        <DatePicker
                            label="To Date"
                            format="YYYY-MM-DD"
                            referenceDate={dcReferenceDateOnly}
                            value={
                                toDate
                                    ? dayjs(toDate)
                                    : null
                            }
                            onChange={(newValue: Dayjs | null) => {
                                const dateValue = newValue ? newValue.format('YYYY-MM-DD') : '';
                                setToDate(dateValue);
                                if (dateFilterError) setDateFilterError(null);
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    placeholder: 'YYYY-MM-DD',
                                    title: 'Date in DC timezone',
                                    sx: {
                                        width: '170px',
                                        '& .MuiInputBase-root': {
                                            fontSize: '0.85rem',
                                            height: '48px',
                                            fontFamily: FONT_FAMILY,
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)'
                                        }
                                    }
                                },
                                day: (ownerState: any) => ({
                                    sx: ownerState.today ? {
                                        border: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        color: 'inherit !important'
                                    } : {}
                                })
                            }}
                        />
                        </LocalizationProvider>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleApplyDateFilter}
                            sx={{
                                height: '42px',
                                minWidth: '90px',
                                fontFamily: FONT_FAMILY,
                                textTransform: 'none',
                                borderRadius: '10px'
                            }}
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearSplitDateFilter}
                            sx={{
                                height: '42px',
                                minWidth: '90px',
                                fontFamily: FONT_FAMILY,
                                textTransform: 'none',
                                borderRadius: '10px'
                            }}
                        >
                            Clear
                        </Button>
                    </Box>
                    {dateFilterError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {dateFilterError}
                        </Alert>
                    )}
                    <Box sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '12px',
                        padding: '12px 20px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        mb: 3
                    }}>
                        <Typography variant="body1" sx={{
                            color: '#333',
                            fontFamily: FONT_FAMILY,
                            fontWeight: 'bold',
                            fontSize: '0.95rem'
                        }}>
                            📊 {filteredSplitWorksheetData.length} Records • {hasAppliedSplitFilter ? groupedSplitWorksheetData.length : 0} Date Groups
                        </Typography>
                    </Box>

                    {splitWorksheetLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <CircularProgress size={60} sx={{ color: '#1976d2', mb: 3 }} />
                                <Typography variant="h6" sx={{
                                    fontFamily: FONT_FAMILY,
                                    color: '#666',
                                    fontWeight: 'bold'
                                }}>
                                    Loading store groups...
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {!splitWorksheetLoading && (
                        <>
                            {!hasAppliedSplitFilter ? (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 8,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    borderRadius: '16px',
                                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                                    mx: 'auto',
                                    maxWidth: '600px'
                                }}>
                                    <Typography variant="h6" sx={{
                                        fontFamily: FONT_FAMILY,
                                        color: '#51606f',
                                        textAlign: 'center',
                                        maxWidth: '460px',
                                        lineHeight: 1.6
                                    }}>
                                        Select a date range and click Apply to view matching route segments.
                                    </Typography>
                                </Box>
                            ) : groupedSplitWorksheetData.length === 0 ? (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 8,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    borderRadius: '16px',
                                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                                    mx: 'auto',
                                    maxWidth: '500px'
                                }}>
                                    <Typography variant="h5" sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontWeight: 'bold',
                                        color: '#1976d2',
                                        mb: 2
                                    }}>
                                        🏪 No Store Records Found
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        fontFamily: FONT_FAMILY,
                                        color: '#666',
                                        textAlign: 'center',
                                        maxWidth: '400px'
                                    }}>
                                        There are currently no split worksheet records to display. Try adjusting your filters or check back later.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
                                    {groupedSplitWorksheetData.map((dateGroup) => {
                                        const isDateExpanded = expandedDateGroups.has(dateGroup.dateKey);

                                        return (
                                            <Accordion
                                                key={dateGroup.dateKey}
                                                expanded={isDateExpanded}
                                                onChange={() => handleDateAccordionToggle(dateGroup.dateKey)}
                                                sx={ACCORDION_STYLES.container}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ChevronDown />}
                                                    sx={{
                                                        ...ACCORDION_STYLES.summary,
                                                        background: 'linear-gradient(135deg, rgba(25,118,210,0.12) 0%, rgba(255,255,255,0.96) 100%)',
                                                        '&:hover': {
                                                            borderColor: 'rgba(25, 118, 210, 0.3)',
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                                        <Typography variant="h6" sx={{
                                                            fontFamily: FONT_FAMILY,
                                                            fontWeight: 'bold',
                                                            color: '#102a43'
                                                        }}>
                                                            {dateGroup.dateLabel}
                                                        </Typography>
                                                        <Chip
                                                            label={`Total Store: ${dateGroup.totalShipments}`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                                color: '#1976d2',
                                                                fontWeight: 'bold',
                                                                fontFamily: FONT_FAMILY
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`Unique Routes: ${dateGroup.uniqueRouteCount}`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: 'rgba(56, 142, 60, 0.1)',
                                                                color: '#388e3c',
                                                                fontWeight: 'bold',
                                                                fontFamily: FONT_FAMILY
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`Cube: ${dateGroup.totalCube.toFixed(2)}`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: 'rgba(245, 124, 0, 0.1)',
                                                                color: '#f57c00',
                                                                fontWeight: 'bold',
                                                                fontFamily: FONT_FAMILY
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`Weight: ${dateGroup.totalWeight.toFixed(2)}`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: 'rgba(93, 64, 55, 0.1)',
                                                                color: '#5d4037',
                                                                fontWeight: 'bold',
                                                                fontFamily: FONT_FAMILY
                                                            }}
                                                        />
                                                    </Box>
                                                </AccordionSummary>

                                                <AccordionDetails sx={ACCORDION_STYLES.details}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, backgroundColor: '#fafcff' }}>
                                                        {dateGroup.stores.map((storeGroup) => {
                                                            const isStoreExpanded = expandedStoreGroups.has(storeGroup.storeKey);
                                                            const storeStyle = getOptimizedStoreBackgroundStyle(storeGroup.store);

                                                            return (
                                                                <Accordion
                                                                    key={storeGroup.storeKey}
                                                                    expanded={isStoreExpanded}
                                                                    onChange={(event) => {
                                                                        event.stopPropagation();
                                                                        handleStoreAccordionToggle(storeGroup.storeKey);
                                                                    }}
                                                                    sx={{
                                                                        ...ACCORDION_STYLES.container,
                                                                        mb: 0,
                                                                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
                                                                        border: '1px solid rgba(15, 23, 42, 0.08)'
                                                                    }}
                                                                >
                                                                    <AccordionSummary
                                                                        expandIcon={<ChevronDown />}
                                                                        onClick={(event) => event.stopPropagation()}
                                                                        sx={{
                                                                            ...ACCORDION_STYLES.summary,
                                                                            borderLeft: `4px solid ${storeStyle.backgroundColor.replace('0.1)', '0.6)')}`,
                                                                            backgroundColor: 'rgba(255, 255, 255, 0.96)'
                                                                        }}
                                                                    >
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
                                                                            <Typography variant="h6" sx={{
                                                                                fontFamily: FONT_FAMILY,
                                                                                fontWeight: 'bold',
                                                                                color: '#333',
                                                                                fontSize: '1rem'
                                                                            }}>
                                                                                Store {storeGroup.store}
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{
                                                                                fontFamily: FONT_FAMILY,
                                                                                color: '#666'
                                                                            }}>
                                                                                Contains {storeGroup.summary.routeCount} Active Route{storeGroup.summary.routeCount === 1 ? '' : 's'}: {storeGroup.summary.uniqueRoutes.join(', ')}
                                                                            </Typography>
                                                                        </Box>
                                                                    </AccordionSummary>

                                                                    <AccordionDetails sx={ACCORDION_STYLES.details}>
                                                                        <TableContainer component={Paper} sx={TABLE_STYLES.container}>
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                                                                                        <TableCell sx={{ ...TABLE_STYLES.headerCell, width: '12%', textAlign: 'center' }}>
                                                                                            Route
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...TABLE_STYLES.headerCell, width: '18%', textAlign: 'center' }}>
                                                                                            Commodity Type
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...TABLE_STYLES.headerCell, width: '20%', textAlign: 'center' }}>
                                                                                            Route Group
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...TABLE_STYLES.headerCell, width: '15%', textAlign: 'center' }}>
                                                                                            Cube
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...TABLE_STYLES.headerCell, width: '15%', textAlign: 'center' }}>
                                                                                            Weight
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...TABLE_STYLES.headerCell, width: '20%', textAlign: 'center' }}>
                                                                                            Timestamp
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {storeGroup.records.map((record) => (
                                                                                        <TableRow key={record.id} hover sx={{
                                                                                            '&:hover': {
                                                                                                backgroundColor: 'rgba(25, 118, 210, 0.02)',
                                                                                            }
                                                                                        }}>
                                                                                            <TableCell sx={{ ...TABLE_STYLES.bodyCell, fontWeight: 'bold', color: '#1976d2', textAlign: 'center' }}>
                                                                                                {record.route}
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ ...TABLE_STYLES.bodyCell, textAlign: 'center' }}>
                                                                                                {record.productTypeCd || '-'}
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ ...TABLE_STYLES.bodyCell, textAlign: 'center' }}>
                                                                                                {record.routeGroupId ?? '-'}
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ ...TABLE_STYLES.bodyCell, textAlign: 'center' }}>
                                                                                                {Number(record.cube).toFixed(2)}
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ ...TABLE_STYLES.bodyCell, textAlign: 'center' }}>
                                                                                                {Number(record.weight).toFixed(2)}
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ ...TABLE_STYLES.bodyCell, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                                                                {formatDate(record.createTs)}
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </TableContainer>
                                                                    </AccordionDetails>
                                                                </Accordion>
                                                            );
                                                        })}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </TabPanel>
       

        {/* View Payload Dialog */}
        <Dialog
            open={viewPayloadDialogOpen}
            onClose={() => {
                setViewPayloadDialogOpen(false);
                setCurrentRouteId(null);
                setJsonSearchTerm('');
            }}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 2,
                    px: 3,
                    borderRadius: '16px 16px 0 0'
                }}
            >
                
                View Route_{viewPayloadData?.RouteId || 'Unknown'} JSON
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {viewPayloadLoading && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                    }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={60} sx={{ color: '#667eea', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, color: '#667eea' }}>
                                Loading Route Data...
                            </Typography>
                        </Box>
                    </Box>
                )}
                {viewPayloadError && (
                    <Box sx={{ p: 3, background: 'linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%)' }}>
                        <Alert
                            severity="error"
                            sx={{
                                fontFamily: FONT_FAMILY,
                                fontSize: '1rem',
                                borderRadius: '12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            {viewPayloadError}
                        </Alert>
                    </Box>
                )}
                {!viewPayloadLoading && !viewPayloadError && viewPayloadData && (
                    <Box sx={{ p: 3, background: '#f8f9fa' }}>

                        {/* JSON Viewer */}
                        <Paper sx={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(0, 0, 0, 0.05)'
                        }}>
                            <Box sx={{
                                p: 2,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>
                                        Raw JSON Data
                                    </Typography>
                                </Box>
                                <TextField
                                    size="small"
                                    placeholder="Search JSON..."
                                    value={jsonSearchTerm}
                                    onChange={(e) => setJsonSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <Search size={16} style={{ marginRight: 8, color: '#667eea' }} />,
                                    }}
                                    sx={{
                                        width: '250px',
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            borderRadius: '8px',
                                            '& fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#667eea',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: '#333',
                                            fontFamily: FONT_FAMILY,
                                            fontSize: '0.875rem',
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: '#666',
                                            opacity: 1,
                                        },
                                    }}
                                />
                            </Box>
                            <Box sx={{
                                p: 3,
                                background: '#1e1e1e',
                                maxHeight: '500px',
                                overflow: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1',
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#888',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        background: '#555',
                                    },
                                },
                            }}>
                                <pre style={{
                                    margin: 0,
                                    fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                                    fontSize: '13px',
                                    lineHeight: '1.5',
                                    color: '#d4d4d4',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all'
                                }}>
                                    {highlightedJson}
                                </pre>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{
                p: 3,
                background: '#f8f9fa',
                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                gap: 1,
                justifyContent: 'space-between'
            }}>
                <Button
                    onClick={() => currentRouteId && handleReplayConfirm(parseInt(currentRouteId))}
                    variant="contained"
                    startIcon={<RefreshCw size={18} />}
                    disabled={!currentRouteId || currentRouteStatus?.toLowerCase() === 'in progress' || currentRouteStatus?.toLowerCase() === 'failed'}
                    sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                        },
                        '&:disabled': {
                            background: '#6c757d',
                            color: '#ffffff',
                        }
                    }}
                >
                    Replay Route
                </Button>
                <Button
                    onClick={() => {
                        setViewPayloadDialogOpen(false);
                        setCurrentRouteId(null);
                    }}
                    variant="contained"
                    sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>

        {/* Replay Confirmation Dialog */}
        <Dialog
            open={replayConfirmDialogOpen}
            onClose={handleReplayConfirmCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" sx={{ fontFamily: FONT_FAMILY, mb: 2 }}>
                    Are you sure you want to continue with the Route Replay?
                </Typography>
            </DialogContent>
            <DialogActions sx={{
                p: 3,
                background: '#f8f9fa',
                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                gap: 1,
                justifyContent: 'center'
            }}>
                <Button
                    onClick={handleReplayConfirmCancel}
                    variant="outlined"
                    sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        borderColor: '#6c757d',
                        color: '#6c757d',
                        '&:hover': {
                            borderColor: '#5a6268',
                            backgroundColor: '#f8f9fa',
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleReplayConfirmContinue}
                    variant="contained"
                    disabled={replayLoading}
                    sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                        },
                        '&:disabled': {
                            background: '#6c757d',
                            color: '#ffffff',
                        }
                    }}
                >
                    {replayLoading ? 'Replaying...' : 'Continue Replay'}
                </Button>
            </DialogActions>
        </Dialog>
        </React.Fragment>
            )
            }
        </Box>
    </>);
};

export default Routing;